import { Button, Card, Col, Row, List, Form, Input, Modal, Avatar, Space, Typography, notification, Divider } from "antd";
import { GameSelect } from "../GameSelect";
import { EventTable } from "../../api.types";
import { t } from "i18next";
import { useState, useContext, useEffect, useImperativeHandle, forwardRef } from "react";
import { CurrentUserContext, useHttp } from "../../Context";
import { UserAddOutlined, UserDeleteOutlined, PlusOutlined } from "@ant-design/icons";

export const Tables = forwardRef((props: { eventId: string, onCreateTable?: (gameId: number, gameName: string, note: string) => any }, ref) => {
    const httpFetch = useHttp()
    const [tables, setTables] = useState<EventTable[]>([]);
    const [guestNameInput, setGuestNameInput] = useState<string>("");
    const [isAddTableModalVisible, setIsAddTableModalVisible] = useState(false);
    const { currentUser, reloadUser } = useContext(CurrentUserContext);

    const isGuest = !currentUser || !currentUser.isGuest
    const username = currentUser ? currentUser.username : ""

    useImperativeHandle(ref, () => ({
        createTable(gameId: number, gameName: string, note: string) {
            createTable(gameId, gameName, note)
        }
    }))

    const submit = (name: string) => {
        httpFetch("/api/users/me", {
            method: "PATCH",
            body: JSON.stringify({
                username: name
            })
        }).then(() => { reloadUser(); loadTables() })
    }

    const loadTables = () => {
        httpFetch("/api/event/" + props.eventId + "/tables").then((resp) => {
            if (resp.ok) {
                resp.json().then((data: EventTable[]) => { setTables(data); })
            }
        })
    }

    const createTable = (gameId: number, gameName: string, note: string) => {
        httpFetch("/api/event/" + props.eventId + "/tables", {
            method: "POST",
            body: JSON.stringify({ id: gameId, name: gameName, note }),
        }).then((resp) => {
            if (resp.ok) {
                resp.json().then((data: EventTable[]) => { setTables(data); })
                notification.success({ message: t("Table created successfully") });
            }
        })
    }
    const handleJoinTable = (tableId: string) => {
        if (isGuest && !username) {
            notification.error({ message: t("You must set your name first") });
            return;
        }
        httpFetch("/api/event/" + props.eventId + "/tables/" + tableId + "/join", {
            method: "POST",
        }).then((resp) => {
            if (resp.ok) {
                resp.json().then((data: EventTable[]) => { setTables(data); })
            }
        })
    }

    const handleDeleteTable = (tableId: string) => {
        httpFetch("/api/event/" + props.eventId + "/tables/" + tableId, {
            method: "DELETE",
        }).then((resp) => {
            if (resp.ok) {
                resp.json().then((data: EventTable[]) => { setTables(data); })
                notification.success({ message: t("Table deleted successfully") });
            }
        })
    };

    useEffect(loadTables, [httpFetch, props.eventId])

    const handleLeaveTable = (tableId: string) => {
        httpFetch("/api/event/" + props.eventId + "/tables/" + tableId + "/leave", {
            method: "DELETE",
        }).then((resp) => {
            if (resp.ok) {
                resp.json().then((data: EventTable[]) => { setTables(data); })
            }
        })
    };

    const handleNameChange = () => {
        const newName = guestNameInput.trim()
        if (!newName) {
            notification.error({ message: t("Name cannot be empty") });
            if (username.length > newName.length) {
                setGuestNameInput(username)
            }
            return;
        }

        if (newName.length < 3) {
            notification.error({ message: t("Name must be at least 3 characters long") });
            if (username.length > newName.length) {
                setGuestNameInput(username)
            }
            return;
        }

        if (newName !== username) {
            submit(newName)
            notification.success({ message: t("Name updated successfully") });
        }
    };

    const tablesSorted = tables.concat([])
    tablesSorted.sort((a, b) => { return Math.sign(b.players.length) - Math.sign(a.players.length) })

    return (
        <>
            {currentUser && !currentUser.isGuest ? undefined :
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Card size="small">
                            <Typography.Text>
                                {t("Your username")}{" "}
                            </Typography.Text>
                            <Input
                                placeholder={t("Enter new name")}
                                value={guestNameInput || username}
                                onChange={(e) => { setGuestNameInput(e.target.value) }}
                                onPressEnter={(e) => { handleNameChange() }}
                                onBlur={(e) => { handleNameChange() }}
                                minLength={2}
                                maxLength={20}
                                style={{ width: 200 }}
                            />
                        </Card>
                    </Col>
                </Row>
            }

            <List
                grid={{ gutter: 16, xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 }}
                dataSource={tablesSorted}
                renderItem={(table) => (
                    <List.Item>
                        <Card
                            title={
                                <Space>
                                    {table.gameProposition.thumbnail && (
                                        <img
                                            src={table.gameProposition.thumbnail}
                                            alt={table.gameProposition.name}
                                            style={{ width: 32, height: 32, objectFit: "contain" }}
                                        />
                                    )}
                                    <a href={`https://boardgamegeek.com/boardgame/${table.gameProposition.gameId}`}>
                                        {table.gameProposition.name}
                                    </a>
                                </Space>
                            }
                        >
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div>
                                    {t("Players")}: {table.players.length}
                                </div>
                                <List
                                    locale={{ emptyText: t("No players") }}
                                    size="small"
                                    dataSource={table.players}
                                    renderItem={player => (
                                        <List.Item>
                                            <Space style={{ width: "100%", justifyContent: "space-between" }}>
                                                <Space>
                                                    <Avatar size="small">{player.name[0].toUpperCase()}</Avatar>
                                                    <span style={{ fontWeight: player.isMe ? "bolder" : "normal" }}>{player.name}</span>
                                                </Space>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                                {table.gameProposition.notes && (
                                    <>
                                        <Divider />
                                        <Typography.Text>
                                            {table.gameProposition.notes}
                                        </Typography.Text>
                                    </>
                                )}
                                <div style={{ marginTop: 8 }}>
                                    {currentUser && table.players.findIndex((p) => {
                                        return p.isMe
                                    }) !== -1 ? (
                                        <Button
                                            danger
                                            icon={<UserDeleteOutlined />}
                                            onClick={() => handleLeaveTable(table.tableId)}
                                            block
                                        >
                                            {t("Leave")}
                                        </Button>
                                    ) : (
                                        <Button
                                            type="primary"
                                            icon={<UserAddOutlined />}
                                            onClick={() => handleJoinTable(table.tableId)}
                                            block
                                        >
                                            {t("Join")}
                                        </Button>
                                    )}
                                </div>
                                {table.players.length === 0 ?
                                    <div style={{ marginTop: 8 }}>
                                        <Button
                                            danger
                                            onClick={() => handleDeleteTable(table.tableId)}
                                            block
                                        >
                                            {t("Delete table")}
                                        </Button>
                                    </div>
                                    : undefined}
                            </Space>
                        </Card>
                    </List.Item>
                )}
            />

            <Row justify="center" style={{ marginTop: 16 }}>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsAddTableModalVisible(true)}
                    >
                        {t("Create table")}
                    </Button>
                </Col>
            </Row>

            <AddTableModal isOpen={isAddTableModalVisible} onCreateTable={createTable} onClose={() => setIsAddTableModalVisible(false)}/>
        </>
    );
})

const AddTableModal = (props: { isOpen: boolean, onCreateTable: (gameId: number, gameName: string, note: string) => any, onClose: () => any }) => {
    const [form] = Form.useForm();

    const handleCreateTable = (values: any) => {
        props.onCreateTable(values.game.id, values.game.name, values.notes)
        props.onClose();
        form.resetFields();
    };

    return <Modal
        title={t("Create New Table")}
        open={props.isOpen}
        onCancel={() => {
            props.onClose()
            form.resetFields();
        }}
        footer={null}
    >
        <Form
            form={form}
            onFinish={handleCreateTable}
            layout="vertical"
        >
            <Form.Item
                name="game"
                label={t("Game")}
                rules={[{ required: true, message: t("Please select a game") }]}
            >
                <GameSelect value={form.getFieldValue("game")} onChange={(game) => form.setFieldValue("game", game)} />
            </Form.Item>

            <Form.Item
                name="notes"
                label={t("Notes")}
            >
                <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" size="large">
                    {t("Create table")}
                </Button>
            </Form.Item>
        </Form>
    </Modal>
}
